import { InMemoryCache } from 'apollo-cache-inmemory';
import gql from 'graphql-tag';

export const APPLICATION_STATE_KEY = 'flytsuite-application-state-v1';
const LEGACY_CACHE_KEY = 'apollo-cache-persist';

// Select UI state explicitly. Session permissions, initialization flags, organization
// documents and remote query results must be loaded again for the current session.
const APPLICATION_STATE_QUERY = gql`
    query PersistedApplicationState {
        highlightFiltered
        paginationLimit
        schedulingCalendarView
        schedulingDateRange
        reportDateRange
        navSiderCollapsed
        checkpointBetaBannerDismissed
        scheduling {
            phase1Step
        }
        globalFilter {
            visible
            customer { key label }
            transporter { key label }
            departure { key label }
            destination { key label }
            employer { key label }
            pax { lastName firstName }
            cgo { name }
        }
        userFilter {
            paxSets { owner startDate endDate }
            scheduledPax { owner startDate endDate }
            scheduledFlights { owner startDate endDate }
        }
        tableSettings {
            name
            settings { visibleColumns }
        }
    }
`;

type StateStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Read selected local fields, including partial state when defaults gain new fields. */
function readApplicationState(cache: InMemoryCache): Record<string, any> {
    return cache.diff<Record<string, any>>({
        query: APPLICATION_STATE_QUERY,
        optimistic: false,
        returnPartialData: true
    }).result;
}

/**
 * Restore and watch application preferences only. Call after seeding cache defaults.
 * The old normalized cache is read once for migration; its server records are discarded.
 */
export function startApplicationStatePersistence(cache: InMemoryCache, storage: StateStorage): () => void {
    try {
        const savedState = storage.getItem(APPLICATION_STATE_KEY);
        const legacyCache = savedState === null ? storage.getItem(LEGACY_CACHE_KEY) : null;
        const saved = savedState === null ? legacyCache : savedState;

        if (saved !== null) {
            // Project both formats through the same selection before touching the live cache.
            const restored = new InMemoryCache();
            if (savedState === null) {
                restored.restore(JSON.parse(saved));
            } else {
                restored.writeData({ data: JSON.parse(saved) });
            }

            cache.writeData({ data: readApplicationState(restored) });
        }
    } catch {
        console.warn('Could not restore application preferences; using defaults.');
    }

    let lastAttempted: string;
    let warned = false;

    const save = () => {
        const serialized = JSON.stringify(readApplicationState(cache));
        if (serialized === lastAttempted) {
            return;
        }
        lastAttempted = serialized;

        try {
            try {
                storage.setItem(APPLICATION_STATE_KEY, serialized);
            } catch (error) {
                if (error.name !== 'QuotaExceededError' || storage.getItem(LEGACY_CACHE_KEY) === null) {
                    throw error;
                }

                // A full origin may need the old server snapshot removed to make room.
                // Its preferences have already been restored into the live cache above.
                storage.removeItem(LEGACY_CACHE_KEY);
                storage.setItem(APPLICATION_STATE_KEY, serialized);
            }

            storage.removeItem(LEGACY_CACHE_KEY);
        } catch {
            // Storage failure must not interrupt working in-memory state or cause a rejection.
            if (!warned) {
                console.warn('Could not save application preferences; changes will last for this session only.');
                warned = true;
            }
        }
    };

    save();

    return cache.watch({
        query: APPLICATION_STATE_QUERY,
        optimistic: false,
        callback: save
    });
}
